import { type FC } from 'react'

import { useTranslation } from 'react-i18next'
import { createGlobalStyle, styled } from 'styled-components'

import { LastPostInfo } from 'components-ref/molecules'
import VideoPlayer from 'features/VideoPlayer/VideoPlayer'
import { useOrientation } from 'helpers/helperHooks'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { renderPostText } from 'helpers/postHelpers'
import { type IFile } from 'types/interfaces/IFile'
import { type IPostFile } from 'types/interfaces/ITypes'

interface Props {
  mediaItem: IFile
  postFile: IPostFile
  minimized: boolean
  isFirstPost: boolean
  isActive: boolean
  showControls: boolean
  videoUserId: string
  toggleControls: () => void
  hasNextPage: boolean
  isLastPost?: boolean
  isInViewport?: boolean
  onPlaybackState?: (state: {
    time: number
    duration: number
    progress: number
    playing: boolean
    level?: number
    ended?: boolean
  }) => void
  onVideoElement?: (
    video: HTMLVideoElement | null
  ) => void
  independentPreviewVisibility?: boolean
}

const SinglePostPreviewVideo: FC<Props> = ({
  mediaItem,
  postFile,
  minimized,
  isActive,
  showControls,
  isInViewport,
  videoUserId,
  toggleControls,
  hasNextPage,
  isLastPost = false,
  onPlaybackState,
  onVideoElement,
  independentPreviewVisibility = false
}) => {
  const deviceOrientation = useOrientation()
  const { t } = useTranslation()

  const isPortrait =
    deviceOrientation?.type?.includes('portrait') ?? true

  const orientation =
    mediaItem.orientation?.toLowerCase() ?? ''

  const shouldPlay =
    !!isInViewport &&
    isActive

  const cachedThumbnail =
    mediaItem.active_thumb
      ? getCachedBlob(mediaItem.active_thumb)
      : null

  const thumbnailSource =
    cachedThumbnail ??
    mediaItem.active_thumb

  return (
    <div
      className={`preview__file ${orientation}`}
    >
      {minimized ? (
        <>
          <img
            className='preview__file__image preview__file__image--minimized'
            src={thumbnailSource}
            alt={t('preview')}
          />

          <div className='preview__minimized__content'>
            <div className='preview__minimized__content--top'>
              <div className='header__avatar'>
                <img
                  src={postFile.minimizedAvatar}
                  alt={t('placeholder')}
                />
              </div>

              <div className='header__name'>
                {postFile.minimizedName}
              </div>
            </div>

            <div className='preview__minimized__content--bottom'>
              <div className='content__text'>
                {renderPostText(
                  postFile.text ?? '',
                  postFile.mentions
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <MediaContainer
          onClick={toggleControls}
        >
          <img
            className='preview__background'
            src={thumbnailSource}
            alt={t('background')}
          />
          {thumbnailSource && !shouldPlay && (
            <img
              className='preview__videoPoster'
              src={thumbnailSource}
              alt=''
              draggable={false}
            />
          )}
          <VideoPlayer
            segment_data={
              mediaItem.aggregated_segment_data
            }
            duration={mediaItem.duration}
            poster={thumbnailSource}
            cloudfront={mediaItem.cloudfront}
            encoded={mediaItem.encoded}
            url={mediaItem.url}
            showControls={showControls}
            minimized={minimized}
            setShowControls={toggleControls}
            isActive={shouldPlay}
            shouldTrackPlayed={shouldPlay}
            videoId={mediaItem.id}
            videoUserId={videoUserId}
            pauseWhenPreviewClosed={
              !independentPreviewVisibility
            }
          />

          <PaginationDots
            isActive={isActive}
          />
        </MediaContainer>
      )}

      {isLastPost &&
        !hasNextPage && (
          <LastPostInfo />
        )}
    </div>
  )
}

export default SinglePostPreviewVideo

const PaginationDots =
  createGlobalStyle<{
    isActive: boolean
  }>`
    .swiper
      > .swiper-pagination.swiper-pagination-bullets-dynamic.swiper-pagination-bullets.swiper-pagination-horizontal {
      ${props =>
        props.isActive
          ? 'bottom: 8.5rem !important;'
          : ''}
    }
  `

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: black;
  overflow: hidden;

  .preview__videoPoster {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    z-index: 2;
    pointer-events: none;
    user-select: none;
  }

  .preview__file__video {
    position: relative;
    z-index: 1;
  }

  .preview__background {
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(24px) brightness(0.7);
    transform: scale(1.1);
    top: 0;
    left: 0;
    pointer-events: none;
    user-select: none;
  }
`